import Controller from './Controller';
import type Report from '../checks/Report';
import { Combined_CreateEvent, Combined_CreateSpecialisedEvent } from '../commands/combined';
import { ensureEvent, uniqueName } from '../commands/metamodel/util';
import { uniq } from 'lodash';
import type { OrId } from '../commands/types';
import type { IEventType } from '../../stores/model/metamodel/event_type';
import type { IInheritance } from '../../stores/model/metamodel/inheritance';

/**
 * Label/value pair surfaced to the inheritance selector in the UI.
 */
interface InheritanceOption {
  label: string;
  value: string;
}

/**
 * Controller to add a (specialised) event type.
 */
export default class AddEventController extends Controller {
  eventName(id: OrId<IEventType>): string {
    const event = ensureEvent(this.modelVersion.content!.metamodel, id);
    return event!.name;
  }

  suggestName(ownerEventTypeOrId: OrId<IEventType>): string {
    return uniqueName(
      this.modelVersion.content!.metamodel.eventNames,
      this.eventName(ownerEventTypeOrId)
    );
  }

  inheritances(ownerEventTypeOrId: OrId<IEventType>): InheritanceOption[] {
    const event = ensureEvent(this.modelVersion.content!.metamodel, ownerEventTypeOrId);
    return uniq(event!.owningObjectTypes.flatMap((ot) => ot.inheritancesAsSupertype.slice())).map(
      (i) => ({
        label: i.utf8DisplayString,
        value: i.id,
      })
    );
  }

  async addEvent(name: string): Promise<Report> {
    return this.executeAndCheck((model) => {
      const { checks } = Combined_CreateEvent(model, name);
      return checks;
    });
  }

  async addSpecialisedEvent(
    name: string,
    ownerEventType: OrId<IEventType>,
    inheritance: OrId<IInheritance>
  ): Promise<Report> {
    return this.executeAndCheck((model) => {
      const { checks } = Combined_CreateSpecialisedEvent(model, name, ownerEventType, inheritance);
      return checks;
    });
  }
}
